import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { IconPlus } from '@tabler/icons-react';

import { Button, Typography } from '@zendesk/forethought-elements';
import { initialInsightParams } from '../dashboard-pages/solve-insights/constants';
import { dateRangeToTimestamp } from '../dashboard-pages/solve-insights/helpers';
import { ProductMode } from '../dashboard-pages/solve-insights/types';
import { INITIAL_EXPRESSION } from './constants';
import ContextVariableExpressionBuilder from './ContextVariableExpressionBuilder';
import {
  addDefaultField,
  filterSupportedContextVariables,
  getQueryExpressionFieldType,
  updateArrayItem,
} from './helper';
import { ContextVariableFilterItemValue } from './types';
import { FieldOption } from 'src/components/expression-builder/ExpressionBuilder';
import { useGetContextVariables } from 'src/hooks/useGetContextVariables';
import { ConditionExpressions } from 'src/services/apiInterfaces';
import { DateRange } from 'src/types/types';

interface ContextVariableFilterBuilderProps {
  error?: string;
  filters: ContextVariableFilterItemValue[];
  onChange: (value: ContextVariableFilterItemValue[]) => void;
  product?: ProductMode;
  uiDateRange: DateRange;
}

const ContextVariableFilterBuilder = ({
  error,
  filters,
  onChange,
  product,
  uiDateRange,
}: ContextVariableFilterBuilderProps) => {
  const { t } = useTranslation('discover');
  const { contextVariables } = useGetContextVariables({
    shouldIncludeSystemContextVariables: true,
    shouldOnlyReturnSupportedFilterableContextVariables: true,
  });
  const fieldOptions: FieldOption[] = useMemo(
    () => filterSupportedContextVariables(contextVariables),
    [contextVariables],
  );

  const handleChange = useCallback(
    (field: Partial<ConditionExpressions>, index: number) => {
      const updatedFilters = updateArrayItem(filters, index, field);
      onChange(updatedFilters);
    },
    [filters, onChange],
  );

  const handleBooleanOperatorChange = useCallback(
    (booleanOperator: string) => {
      const items = filters.map(item => {
        return {
          ...item,
          boolean_operator: booleanOperator,
        };
      });
      onChange(items);
    },
    [filters, onChange],
  );

  const handleAdd = useCallback(() => {
    const target = filters[filters.length - 1];
    const booleanOperator = target?.boolean_operator || 'and';
    const expression = addDefaultField(
      fieldOptions,
      INITIAL_EXPRESSION.expression,
    );
    const items = filters.concat({
      boolean_operator: booleanOperator,
      expression,
    });
    onChange(items);
  }, [fieldOptions, filters, onChange]);

  const handleDelete = useCallback(
    (index: number) => {
      const items = filters.filter((_, i) => i !== index);
      onChange(items);
    },
    [filters, onChange],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: 2,
      }}
    >
      <Typography variant='font14Bold'>
        {t('contextVariableFilterBuilder.title')}
      </Typography>
      <Button
        onClick={handleAdd}
        startIcon={<IconPlus fontSize='small' />}
        variant='secondary'
      >
        {t('contextVariableFilterBuilder.addButton')}
      </Button>
      {Boolean(error) && (
        <Typography color='error' variant='font12'>
          {error}
        </Typography>
      )}
      {filters.map((item, index) => {
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: 1,
            }}
          >
            <ContextVariableExpressionBuilder
              booleanOperator={index === 0 ? undefined : item.boolean_operator}
              expression={addDefaultField(fieldOptions, item.expression)}
              field={item.expression.field}
              fieldOptions={fieldOptions}
              fieldType={getQueryExpressionFieldType(
                item.expression.field,
                fieldOptions,
              )}
              index={index}
              isBooleanOperatorDisabled={false || index > 1}
              isDeleteButtonDisabled={false}
              onDeleteButtonClick={() => handleDelete(index)}
              onSelectBooleanOperator={booleanOperator =>
                handleBooleanOperatorChange(booleanOperator)
              }
              onSelectedField={field =>
                handleChange({ field, values: [] }, index)
              }
              onSelectOperator={operator => {
                let transformedValue: string[] = [];
                if (operator?.value === 'between') {
                  // Add default value for date if date between is selected
                  const timestamps = dateRangeToTimestamp(
                    initialInsightParams.date,
                  );
                  transformedValue = [
                    timestamps.start_timestamp.toString() ?? '',
                    timestamps.end_timestamp.toString() ?? '',
                  ];
                }

                handleChange(
                  {
                    negate: operator.negate,
                    operator: operator.value,
                    values: transformedValue,
                  },
                  index,
                );
              }}
              onValueChange={value => {
                const values = Array.isArray(value) ? value : [value];
                handleChange({ values }, index);
              }}
              product={product}
              uiDateRange={uiDateRange}
            />
          </Box>
        );
      })}
    </Box>
  );
};
export default ContextVariableFilterBuilder;
